<template>
	<component
		:is="element"
		:class="`inline-flex items-center justify-center text-sm font-semibold gap-2 cursor-pointer rounded-lg ${size} ${variation} disabled:opacity-30 disabled:pointer-events-none disabled:cursor-not-allowed`"
		:disabled="status === 'loading'"
		:for="formName"
		tabindex="0"
	>
		<Icon v-if="icon" :name="icon" size="20" class="shrink-0" />
		<slot />
		<Icon name="svg-spinners:180-ring" v-if="status === 'loading'" />
		<Icon name="mdi:check-circle" v-else-if="status === 'success'" />
		<Icon name="mdi:close-circle" v-else-if="status === 'error'" />
	</component>
</template>

<script setup>
const props = defineProps({
	formName: {
		type: String,
		default: null
	},
	loading: {
		type: Boolean,
		default: false
	},
	variation: {
		type: String,
		default: 'primary'
	},
	size: {
		type: String,
		default: 'md'
	},
	icon: {
		type: String
	}
})

const status = ref(null)
let clearStatusTimeout

const VARIATIONS = {
	primary: 'bg-gray-800 text-white hover:bg-black',
	secondary: 'bg-white text-gray-800 hover:bg-gray-200',
	tertiary: 'bg-gray-200 text-gray-800 hover:bg-gray-300',
	destructive: 'bg-red-500 text-white hover:bg-red-600'
}

const SIZES = {
	sm: 'text-sm py-1 px-3',
	md: 'text-base py-2 px-4',
	lg: 'text-lg py-3 px-6'
}

const variation = computed(() => `${VARIATIONS[props.variation]}`)
const size = computed(() => `${SIZES[props.size]}`)

const setStatus = (newStatus, statusClearTimeout = 0) => {
	status.value = newStatus

	if (statusClearTimeout === 0) {
		return
	}

	clearStatusTimeout = setTimeout(() => {
		status.value = null
	}, statusClearTimeout)
}

watch(
	() => props.loading,
	(isLoading) => {
		if (isLoading) {
			status.value = 'loading'

			clearTimeout(clearStatusTimeout)
		} else if (!isLoading && status.value === 'loading') {
			status.value = null

			clearTimeout(clearStatusTimeout)
		}
	}
)

const element = computed(() => {
	if (props.formName) {
		return 'label'
	}

	return 'button'
})

defineExpose({
	setStatus
})
</script>
